import { EquipmentType, isNonSmartChair } from '../interfaces/model';

// AER-1795 / AER-1796: which serial-number type codes a model accepts lives on the model
// record (`snTypeCodes`), not in a hardcoded frontend table, so a model added to the
// database is buildable without a frontend deploy. This module holds the defaults. It is
// shared by the client (Edit Model), the server (createModel) and the backfill script
// (scripts/backfill-sn-type-codes.ts), so it must stay free of UI and server imports.
//
// It only says WHICH codes are acceptable. WHERE the code sits in a serial differs per
// equipment type (chars 1-3 for utility, the last 3 for a handpiece, the first 3 for a
// smart chair, the first 2 for a non-smart chair); those position rules stay with the
// per-type checks in src/utils/serial-number.ts.

// Marks a model whose serials follow no DentalEZ format: third-party sterilizers
// (Tuttnauer), Aeras One retrofits (Midmark, Air Techniques, Gardner Denver, ...) and
// partner smart chairs. It is stored explicitly so "accept any serial" is a recorded
// decision rather than a missing field. Validation skips the type-code, date and length
// checks for these models (QA survey, 2026-09-16: their serials share no token or date
// layout, and several run 13-14 characters).
export const SN_TYPE_CODE_ANY = '*';

// Record<EquipmentType, ...> makes the compiler reject a new equipment type that has no
// entry, so no type can silently end up without codes.
export const SN_TYPE_CODES_BY_TYPE: Record<EquipmentType, string[]> = {
  // DentalEZ moved compressors from CR to CS; both are live (ACR2609104, ACS2508122).
  compressor: ['CR', 'CS'],
  vacuum: ['BN', 'QT'],
  handpiece: ['AH8'],
  chair: ['ABU'],
  sterilizer: [SN_TYPE_CODE_ANY],
  aerasone_compressor: [SN_TYPE_CODE_ANY],
  aerasone_vacuum: [SN_TYPE_CODE_ANY],
};

// Non-smart Forest chairs (5400/CRK) take the placeholder serial `00` + 6 alphanumerics.
export const NON_SMART_CHAIR_SN_TYPE_CODES = ['00'];

// Chair models the partner integration creates (server/src/controllers/external/smartchair.ts)
// carry this family, and their units carry the partner's serials (SERIAL_2, B145737).
export const PARTNER_CHAIR_FAMILY = 'smartchair';

export interface SnTypeCodeSource {
  id?: string;
  type?: string;
  family?: string;
  snTypeCodes?: unknown;
}

// Trimmed, uppercased, de-duplicated. Anything that is not a list of strings (or a
// comma-separated string) yields [], which callers treat as "not supplied".
export const normalizeSnTypeCodes = (codes: unknown): string[] => {
  const list: unknown[] = Array.isArray(codes)
    ? codes
    : typeof codes === 'string'
      ? codes.split(',')
      : [];
  const out: string[] = [];
  list.forEach((c) => {
    const code = typeof c === 'string' ? c.trim().toUpperCase() : '';
    if (code && out.indexOf(code) === -1) {
      out.push(code);
    }
  });
  return out;
};

export const isKnownEquipmentType = (type: unknown): type is EquipmentType =>
  typeof type === 'string' &&
  Object.prototype.hasOwnProperty.call(SN_TYPE_CODES_BY_TYPE, type);

// The codes a model gets when nobody supplied any. undefined only for a type outside
// EquipmentType, which has no rule to derive from.
export const defaultSnTypeCodes = (
  model: SnTypeCodeSource
): string[] | undefined => {
  const type = model && model.type;
  if (!isKnownEquipmentType(type)) {
    return undefined;
  }
  if (type === 'chair') {
    if (isNonSmartChair(model.id)) {
      return [...NON_SMART_CHAIR_SN_TYPE_CODES];
    }
    if (`${model.family || ''}`.trim().toLowerCase() === PARTNER_CHAIR_FAMILY) {
      return [SN_TYPE_CODE_ANY];
    }
  }
  return [...SN_TYPE_CODES_BY_TYPE[type]];
};

// AER-1796: explicitly supplied codes win over the derived default, because some models'
// serial conventions do not follow their type. An empty or missing list counts as not
// supplied, so a stored model never ends up with no codes (validation fails closed on
// that). Throws for an unknown type rather than storing a model nothing can validate.
export const resolveSnTypeCodes = (model: SnTypeCodeSource): string[] => {
  const explicit = normalizeSnTypeCodes(model && model.snTypeCodes);
  if (explicit.length) {
    return explicit;
  }
  const derived = defaultSnTypeCodes(model);
  if (!derived) {
    throw new Error(
      `Cannot set serial number type codes for model ${model && model.id}: unknown equipment type "${model && model.type}"`
    );
  }
  return derived;
};

export const withSnTypeCodes = <T extends SnTypeCodeSource>(
  model: T
): T & { snTypeCodes: string[] } => {
  if (!model) {
    throw new Error('Missing model');
  }
  return { ...model, snTypeCodes: resolveSnTypeCodes(model) };
};

export const acceptsAnySerial = (codes?: string[]): boolean =>
  (codes || []).indexOf(SN_TYPE_CODE_ANY) > -1;
